import { GraphQLSchema } from 'graphql';
import { findLastIndex, omit, zip } from 'lodash';
import { memoize } from 'lodash/fp';
import * as monacoEditor from 'monaco-editor';

import { SelectionPathNode } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import {
  GraphqlDefinition,
  ParseState,
  getAncestorStates,
  getDocumentDefinitions,
  getUsedFragments,
  runOnlineParser,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import {
  exclusiveContainsPosition,
  invertRangesInDocument,
} from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import {
  OperationPath,
  OperationPathSegment,
  operationPathToGraphQLPath,
  parsePathToOperationPath,
  serializeOperationPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { getRootTypeNames, unwrapType } from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

/**
 * Infer the indent level for a given position
 */
function inferIndentLevel(
  position: monacoEditor.Position,
  operationTextLines: string[],
): number {
  const previousLine = operationTextLines[position.lineNumber - 1];
  return Math.floor((previousLine?.match(/^\s*/)?.[0]?.length ?? 0) / 2) + 1;
}

/**
 * Given a state and a path, return the shared path prefix, and the remainder of
 * the docs path
 */
function doesPathMatch(
  locationState: ParseState,
  docsPath: SelectionPathNode[],
  schema: GraphQLSchema,
): {
  matched: boolean;
  remainingDocsPath: SelectionPathNode[];
  depth: number;
} {
  const editorPath = parsePathToOperationPath(
    getAncestorStates(locationState),
  ).filter(
    // Inline fragments without type should not affect matches
    // it is as if they don't exist
    (pathSegment) =>
      !(pathSegment.kind === 'InlineFragment' && !pathSegment.type),
  );

  const definitionDocsPath = operationPathToGraphQLPath(editorPath, schema);

  if (isMinLengthArray(1, definitionDocsPath)) {
    if (definitionDocsPath[0].name === 'root') {
      definitionDocsPath.shift();
    }

    let matchEndIndex = zip(docsPath, definitionDocsPath).findIndex(
      ([docsNode, definitionNode], i) =>
        docsNode && i > 0 && docsNode.name !== definitionNode?.name,
    );

    // nothing in the docsPath didn't match, so matchEndIndex is the last index + 1
    if (matchEndIndex < 0) matchEndIndex = docsPath.length;

    // It is a match if there were no more nodes in definitionDocsPath after the last node
    return {
      matched: matchEndIndex >= definitionDocsPath.length,
      remainingDocsPath: docsPath.slice(matchEndIndex),
      depth: editorPath.length,
    };
  }

  return {
    matched: false,
    remainingDocsPath: docsPath,
    depth: editorPath.length,
  };
}

type ArgumentSelection = {
  name: string;
  range: monacoEditor.Range;
} & ({ isVariable: true; value: string } | { isVariable: false; value: null });
/**
 * This represents the different types of matches
 * - A full match is when the entire docs path is represented in the editor, so
 *   there may be some fields present.
 * - A partial match is when the full docs path is not represented in the editor
 *   meaning there is no selection set.
 * - No match is when the root type does not match what is open in the docs, ie
 *   having a query in the editor and the mutation type in the docs.
 */
export type CurrentDocsContext = (
  | { matchType: 'full' | 'root'; selectionSet: Selection[] }
  | { matchType: 'none' | 'partial' }
) & {
  indentLevel: number;
  remainingDocsPath: SelectionPathNode[];
  writePosition: monacoEditor.Position;
  variables: Array<{
    name: string;
    range: monacoEditor.Range;
    usageCount: number;
  }>;
  // Used for adding arguments to an existing field
  argumentsWritePosition?: monacoEditor.Position;
  argumentsRange?: monacoEditor.Range;
  arguments: Array<ArgumentSelection>;
  variableDefinitionRange?: monacoEditor.Range;
  variableOperationCount: Record<string, number>;
  variableWritePosition?: monacoEditor.Position;
  definitionKind?:
    | 'Query'
    | 'ShortQuery'
    | 'Mutation'
    | 'Subscription'
    | 'FragmentDefinition';
};

export interface Selection {
  name: string | null | undefined;
  type: 'field' | 'inline fragment' | 'operation';
  range: monacoEditor.Range;
  variableUsage?: { [variable: string]: number };
}

interface Path
  extends Pick<
    CurrentDocsContext,
    'arguments' | 'argumentsRange' | 'writePosition' | 'argumentsWritePosition'
  > {
  range: monacoEditor.Range;
  match: ReturnType<typeof doesPathMatch>;
  selectionSet?: Selection[];
  isUntypedInlineFragment: boolean;
  closestTypedParentKey: string | undefined;
}

/**
 * Why do we have two types here?
 *
 * While the parser is running, we have a special
 * type for inline fragments. The identifier for any field or inline fragment
 * in `partialPaths` is their path. An operation can, at any time, have the same
 * field or inline fragments as siblings, and siblings have the same path.
 * We overwrite the paths for fields, and match the first sibling.
 * However, for inline fragments, we make a match based on cursor position, so
 * we need to record all the siblings & find the one that contains the cursor.
 * PartialPathsWithMultipleInlineFragments is the type with an array of possible
 * inline fragment siblings for any given path. Then, we remove the array of
 * possible inline fragments to get PartialPaths.
 */
type InlineFragmentPartialPath = Pick<
  Path,
  'isUntypedInlineFragment' | 'closestTypedParentKey'
> & {
  inlineFragmentSiblings: Omit<
    PartialPath,
    'isUntypedInlineFragment' | 'closestTypedParentKey'
  >[];
  closestUntypedInlineFragmentKey: string;
};
type PartialPathsWithMultipleInlineFragments = Record<
  string,
  PartialPath | InlineFragmentPartialPath
>;

type PartialPath = Partial<Path> &
  Omit<Path, 'range' | 'match' | 'writePosition'>;

/**
 * Get the current docs context for a given position in the editor and docs. See
 * CurrentDocsContext for more details on exactly the shape this returns.
 *
 * What we want to end up with here is the deepest matching path between the
 * editor and docs which is contained by the longest matching prefix path
 * between the cursor and docs (Examples follow).
 *
 * Given a docs path of
 * 'query/a/b'
 * and an editor state of
 * ```
 *   query {
 *     a1: 1 {
 *       b {
 *         foo
 *       }
 *     }
 *     a2: a {
 *       |cursor|
 *     }
 *   }
 * ```
 * It makes sense to show the docs path matching where the cursor is.
 *
 * However given a docs path of
 * 'query/a/b'
 * and an editor state of
 * ```
 *   query {
 *     a1: 1 {
 *       b {
 *         foo
 *       }
 *     }
 *     c {
 *       |cursor|
 *     }
 *   }
 * ```
 * It makes sense to show the docs path matching a1, as the common ancestor of
 * the path to cursor and docs path contains a child path matching query/a/b.
 * Taking the cursor path here and showing no selections for the selection set
 * in b feels like a really broken experience.
 *
 * We keep track of all selection sets that match some part of the docs, as well
 * as for any full docs match (like 'query/a/b' above) we want to keep track of
 * what fields exist in that selection set.
 *
 * After the parser is run we can find the selection set which is contained by
 * our cursorAndDocsCommonAncestor that has the longest matching prefix with the
 * docs path. If this happens to be a full match it will include the currently
 * selected fields and we will return this with our 'full' match. Otherwise we
 * will return a 'partial' match with just the node.
 */
export function getCurrentDocsContext(
  model: monacoEditor.editor.ITextModel,
  /**
   * We often pass only the active parts of the document to getCurrentDocsContext
   * and we want to make sure we get meta information about the entire document,
   * not just the first definition. Instead of needing the calculate a cursor position
   * that is inside the Query/Mutation and not a fragment, you can pass `first-non-fragment`
   * as the position, and it will pick the first Query|Mutation
   */
  cursorPositionInput: monacoEditor.Position | 'first-non-fragment',
  fullDocsPath: SelectionPathNode[],
  schema: GraphQLSchema,
): CurrentDocsContext {
  const modelValue = model.isDisposed() ? '' : model.getValue();
  const { operationDefinitions, fragmentDefinitions, variableOperationCount } =
    getDocumentDefinitions(modelValue, getRootTypeNames(schema));

  const allDefinitions: GraphqlDefinition[] = [
    ...operationDefinitions,
    ...fragmentDefinitions,
  ];

  const cursorPosition =
    cursorPositionInput === 'first-non-fragment'
      ? (operationDefinitions[0]?.range.getStartPosition() ??
        new monacoEditor.Position(1, 1))
      : cursorPositionInput;

  const selectedDefinition =
    allDefinitions.length === 1
      ? allDefinitions[0]
      : allDefinitions.find((definition) =>
          definition.range.containsPosition(cursorPosition),
        );

  // No docs path. Check which root types are included
  if (fullDocsPath.length === 0) {
    if (!selectedDefinition) {
      return {
        matchType: 'root',
        selectionSet: allDefinitions.map((definition) => ({
          name: definition.type,
          type: 'operation',
          range: definition.range,
        })),
        writePosition: cursorPosition,
        remainingDocsPath: [],
        indentLevel: 0,
        variables: [],
        variableOperationCount,
        arguments: [],
      };
    }
  }

  // Not inside any operation and not the root path, no match
  if (!selectedDefinition) {
    return {
      matchType: 'none',
      indentLevel: 0,
      remainingDocsPath: fullDocsPath,
      writePosition: cursorPosition,
      variables: [],
      variableOperationCount,
      arguments: [],
    };
  }

  const matchStartIndex = fullDocsPath.findIndex(
    (docsNode) => unwrapType(docsNode).name === selectedDefinition.type,
  );
  let docsPath = fullDocsPath;
  if (selectedDefinition.kind === 'fragment' && matchStartIndex >= 0) {
    docsPath = fullDocsPath.slice(matchStartIndex);
  } else if (
    isMinLengthArray(1, fullDocsPath) &&
    fullDocsPath[0].name !== selectedDefinition.type
  ) {
    // Mismatched roots
    return {
      matchType: 'none',
      indentLevel: 0,
      remainingDocsPath: fullDocsPath,
      writePosition: selectedDefinition.range.getEndPosition(),
      variables: [],
      variableOperationCount,
      arguments: [],
    };
  }

  const inactiveRanges = invertRangesInDocument(model, [
    selectedDefinition.range,
    ...(selectedDefinition.kind === 'operation'
      ? getUsedFragments(model, selectedDefinition, fragmentDefinitions).map(
          (fragment) => fragment.range,
        )
      : []),
  ]);

  // Phew. If we got here it means that we have a current definition, and that
  // it matches our base path, we can now proceed to try to find the best
  // matching path in the definition.

  // We build up the paths from partialPaths to full paths as the parser runs.
  // For example, when you get to a type with a selection set, we need to parse
  // each type in the selection set and add them to the parent.

  // Record from path to range and selections
  const partialPaths: PartialPathsWithMultipleInlineFragments = {};
  const variables: CurrentDocsContext['variables'] = [];
  const variableUsage: Record<string, number> = {};
  let variableDefinitionRange: monacoEditor.Range | undefined;
  let variableWritePosition: monacoEditor.Position | undefined;
  let definitionKind: CurrentDocsContext['definitionKind'];
  // The innermost inline fragment containing the cursor that also matches our
  // docs state. We track this so that when we are finding the best match, if there
  // is an inline fragment with a cursor in it, we only look for matches inside of it.
  let innerMostMatchingInlineFragmentWithCursor:
    | {
        state: ParseState;
        range: monacoEditor.Range;
      }
    | undefined;

  const isUntypedInlineFragment = (node: OperationPathSegment | undefined) =>
    !!(node && node.kind === 'InlineFragment' && !node.type);

  const getClosestUntypedInlineFragmentKey = (path: OperationPath) =>
    serializeOperationPath(
      path.slice(
        0,
        findLastIndex(path, (node) => isUntypedInlineFragment(node)) + 1,
      ),
    );

  const getClosestTypedParentKey = (path: OperationPath) => {
    const indexOfClosestTypedParentKey = findLastIndex(
      path.slice(0, -1),
      (node) => !isUntypedInlineFragment(node),
    );
    return indexOfClosestTypedParentKey === -1
      ? undefined
      : serializeOperationPath(path.slice(0, indexOfClosestTypedParentKey + 1));
  };

  /**
   * We use the existence of 'range' in this path to know if
   * we have visited the InlineFragment node for the most recent
   * sibling in the list. If we have, we should write a new element
   * to the sibling array. If we haven't, we are updating that
   * most recent inline fragment sibling.
   *
   * Why would we have not visited it yet? We add items in the selection
   * set to parent paths before we visit them.
   */
  const makeNewInlineFragmentSiblingIfPreviousSiblingVisited = (
    path: InlineFragmentPartialPath,
  ) => {
    if (
      path.inlineFragmentSiblings[path.inlineFragmentSiblings.length - 1]?.range
    ) {
      path.inlineFragmentSiblings.push({ arguments: [] });
    }
  };

  const updateLastSibling = ({
    path,
    pathKey,
    newFields,
  }: {
    path: InlineFragmentPartialPath;
    pathKey: string;
    newFields: Partial<
      InlineFragmentPartialPath['inlineFragmentSiblings'][number]
    >;
  }) => {
    makeNewInlineFragmentSiblingIfPreviousSiblingVisited(path);
    const newLastSibling = {
      arguments: [],
      ...newFields,
      ...(path.inlineFragmentSiblings[path.inlineFragmentSiblings.length - 1] ??
        {}),
    };
    partialPaths[pathKey] = {
      ...path,
      inlineFragmentSiblings: [
        ...(path.inlineFragmentSiblings.slice(0, -1) ?? []),
        newLastSibling,
      ],
    };
  };

  runOnlineParser(modelValue, (state, range, tokens) => {
    if (
      inactiveRanges.some((inactiveRange) => inactiveRange.containsRange(range))
    ) {
      return;
    }

    switch (state.kind) {
      case 'AliasedField':
      case 'Field':
      case 'InlineFragment': {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const parentState = state.prevState!;
        const editorNodes = parsePathToOperationPath(getAncestorStates(state));
        const containsUntypedInlineFragment = editorNodes.some((node) =>
          isUntypedInlineFragment(node),
        );
        const parentEditorNodes = editorNodes.slice(0, -1);

        const match = doesPathMatch(state, docsPath, schema);

        // keep track of the tightest inline fragment with the cursor in it
        if (
          match.matched &&
          isUntypedInlineFragment(
            state as Extract<typeof state, { kind: 'InlineFragment' }>,
          ) &&
          exclusiveContainsPosition(range, cursorPosition) &&
          (!innerMostMatchingInlineFragmentWithCursor ||
            innerMostMatchingInlineFragmentWithCursor.range.containsRange(
              range,
            ))
        ) {
          innerMostMatchingInlineFragmentWithCursor = { state, range };
        }

        const parentMatch = doesPathMatch(parentState, docsPath, schema);

        if (parentMatch.matched) {
          let path = partialPaths[serializeOperationPath(parentEditorNodes)];
          if (!path) {
            const parentContainsUntypedInlineFragment = parentEditorNodes.some(
              (node) => isUntypedInlineFragment(node),
            );
            const newPath = {
              ...(parentContainsUntypedInlineFragment
                ? {
                    inlineFragmentSiblings: [],
                    closestUntypedInlineFragmentKey:
                      getClosestUntypedInlineFragmentKey(parentEditorNodes),
                  }
                : { arguments: [] }),
              isUntypedInlineFragment: isUntypedInlineFragment(
                parentEditorNodes[parentEditorNodes.length - 1],
              ),
              closestTypedParentKey:
                getClosestTypedParentKey(parentEditorNodes),
            };
            path = newPath;
            partialPaths[serializeOperationPath(parentEditorNodes)] = path;
          }
          // Add to set
          if (parentMatch.remainingDocsPath.length === 0) {
            const name =
              (state.kind === 'InlineFragment' ? state.type : state.name) ?? '';
            const type =
              state.kind === 'InlineFragment' ? 'inline fragment' : 'field';
            const selection: Selection = {
              name,
              type,
              range,
              variableUsage: tokens.reduce(
                (acc: NonNullable<Selection['variableUsage']>, token) => {
                  if (token.state.kind === 'Variable' && token.state.name) {
                    return {
                      ...acc,
                      [token.state.name]: (acc[token.state.name] ?? 0) + 1,
                    };
                  }
                  return acc;
                },
                {},
              ),
            };

            if ('inlineFragmentSiblings' in path) {
              makeNewInlineFragmentSiblingIfPreviousSiblingVisited(path);
              path.inlineFragmentSiblings = [
                ...(path.inlineFragmentSiblings.slice(0, -1) ?? []),
                {
                  arguments: [],
                  ...path.inlineFragmentSiblings[
                    path.inlineFragmentSiblings.length - 1
                  ],
                  selectionSet: [
                    ...(path.inlineFragmentSiblings[
                      path.inlineFragmentSiblings.length - 1
                    ]?.selectionSet ?? []),
                    selection,
                  ],
                },
              ];
            } else {
              path.selectionSet = [...(path.selectionSet ?? []), selection];
            }
          }
        }

        if (match.matched) {
          // If this is a field, find the token right before the end of the field.
          // This will be used to get the position to write arguments if they need
          // to be added to this field.
          const token =
            state.kind === 'Field' || state.kind === 'AliasedField'
              ? tokens.find((_token, i) => {
                  const nextToken = tokens[i + 1];
                  return !nextToken || nextToken.state.kind !== 'Field';
                })
              : undefined;

          const pathKey = serializeOperationPath(editorNodes);
          const argumentsWritePosition = token?.range.getEndPosition();
          if (containsUntypedInlineFragment) {
            const path = getOrSetRecord(
              pathKey,
              {
                isUntypedInlineFragment: isUntypedInlineFragment(
                  editorNodes[editorNodes.length - 1],
                ),
                closestUntypedInlineFragmentKey:
                  getClosestUntypedInlineFragmentKey(editorNodes),
                closestTypedParentKey: getClosestTypedParentKey(editorNodes),
                inlineFragmentSiblings: [
                  {
                    arguments: [],
                  },
                ],
              },
              partialPaths,
            );
            if ('inlineFragmentSiblings' in path) {
              const newFields = {
                range,
                match,
                writePosition: model.getPositionAt(
                  model.getOffsetAt(range.getEndPosition()) - 1,
                ),
                arguments: [],
                argumentsWritePosition,
              };
              updateLastSibling({ newFields, path, pathKey });
            }
          } else {
            partialPaths[pathKey] = {
              range,
              match,
              argumentsWritePosition,
              writePosition: model.getPositionAt(
                model.getOffsetAt(range.getEndPosition()) - 1,
              ),
              ...(partialPaths[serializeOperationPath(editorNodes)] ?? {
                arguments: [],
                // we check if the path contains an untyped inline fragment above,
                // if it doesn't this path definitely can't be an untyped inline fragment
                isUntypedInlineFragment: false,
                closestTypedParentKey: getClosestTypedParentKey(editorNodes),
              }),
            };
          }
        }
        break;
      }
      case 'Query':
      case 'ShortQuery':
      case 'Mutation':
      case 'Subscription':
      case 'FragmentDefinition': {
        definitionKind = state.kind;
        const editorNodes = parsePathToOperationPath(getAncestorStates(state));
        const pathKey = serializeOperationPath(editorNodes);
        if (state.kind === 'ShortQuery') {
          variableWritePosition = range.getStartPosition();
        } else if (state.kind !== 'FragmentDefinition') {
          // find the last token before the operation definition selection so we can write variables there
          // in this case we want the end of "QueryName" `query QueryName {}` and in this case we want the end of query `query {}`
          const token = tokens.find((_token, i) => {
            const netToken = tokens[i + 1];
            return (
              !netToken ||
              !['Query', 'Mutation', 'Subscription'].includes(
                netToken.state.kind ?? '',
              )
            );
          });
          if (token) {
            variableWritePosition = token.range.getEndPosition();
          } else if (!partialPaths[pathKey]) {
            variableWritePosition = range.getEndPosition();
          }
        }

        const match = doesPathMatch(state, docsPath, schema);
        const containsUntypedInlineFragment = editorNodes.some((node) =>
          isUntypedInlineFragment(node),
        );
        if (match.matched) {
          if (containsUntypedInlineFragment) {
            const path = getOrSetRecord(
              pathKey,
              {
                isUntypedInlineFragment: isUntypedInlineFragment(
                  editorNodes[editorNodes.length - 1],
                ),
                closestUntypedInlineFragmentKey:
                  getClosestUntypedInlineFragmentKey(editorNodes),
                closestTypedParentKey: getClosestTypedParentKey(editorNodes),
                inlineFragmentSiblings: [{ arguments: [] }],
              },
              partialPaths,
            );
            if ('inlineFragmentSiblings' in path) {
              const newFields = {
                range,
                match,
                writePosition: model.getPositionAt(
                  model.getOffsetAt(range.getEndPosition()) - 1,
                ),
                arguments: [],
              };
              updateLastSibling({ newFields, path, pathKey });
            }
          } else {
            partialPaths[pathKey] = {
              range,
              match,
              writePosition: model.getPositionAt(
                model.getOffsetAt(range.getEndPosition()) - 1,
              ),
              ...(partialPaths[pathKey] ?? {
                arguments: [],
                closestTypedParentKey: getClosestTypedParentKey(editorNodes),
                isUntypedInlineFragment: isUntypedInlineFragment(
                  editorNodes[editorNodes.length - 1],
                ),
              }),
            };
          }
        }
        break;
      }
      case 'VariableDefinition': {
        const variableName = tokens.find(
          (t) => t.state.name && t.state.kind === 'Variable',
        )?.state.name;
        if (variableName) {
          variables.push({
            name: variableName,
            range,
            usageCount: variableUsage[variableName] ?? 0,
          });
        }
        break;
      }
      case 'Variable':
        if (state.prevState?.kind !== 'VariableDefinition') {
          const variable = variables.find(({ name }) => name === state.name);
          if (variable) {
            variable.usageCount += 1;
          } else if (state.name) {
            variableUsage[state.name] = (variableUsage[state.name] ?? 0) + 1;
          }
        }
        break;

      case 'Arguments': {
        const fieldState = state.prevState;
        if (!fieldState) {
          // shouldn't happen, arguments should always have prevState
          throw new Error('missing prevState');
        }
        const parentMatch = doesPathMatch(fieldState, docsPath, schema);
        if (parentMatch.matched && parentMatch.remainingDocsPath.length === 0) {
          const editorNodes = parsePathToOperationPath(
            getAncestorStates(fieldState),
          );
          const pathKey = serializeOperationPath(editorNodes);

          let path = partialPaths[pathKey];
          if (!path) {
            const containsUntypedInlineFragment = editorNodes.some(
              (node) => node.kind === 'InlineFragment' && !node.type,
            );
            const newPath = {
              ...(containsUntypedInlineFragment
                ? {
                    inlineFragmentSiblings: [],
                    closestUntypedInlineFragmentKey:
                      getClosestUntypedInlineFragmentKey(editorNodes),
                  }
                : { arguments: [] }),
              isUntypedInlineFragment: isUntypedInlineFragment(
                editorNodes[editorNodes.length - 1],
              ),
              closestTypedParentKey: getClosestTypedParentKey(editorNodes),
            };
            path = newPath;
          }
          partialPaths[pathKey] = path;

          if ('inlineFragmentSiblings' in path) {
            makeNewInlineFragmentSiblingIfPreviousSiblingVisited(path);
            const currentValue =
              path.inlineFragmentSiblings[
                path.inlineFragmentSiblings.length - 1
              ];
            const newFields = {
              argumentsRange: range,
              argumentsWritePosition:
                currentValue?.argumentsWritePosition ??
                new monacoEditor.Position(
                  range.startLineNumber,
                  range.startColumn + 1,
                ),
            };
            path.inlineFragmentSiblings = [
              ...path.inlineFragmentSiblings.slice(0, -1),
              {
                ...{ arguments: [] },
                ...path.inlineFragmentSiblings[
                  path.inlineFragmentSiblings.length - 1
                ],
                ...newFields,
              },
            ];
          } else {
            const currentValue = path;
            const newFields = {
              argumentsRange: range,
              argumentsWritePosition:
                currentValue?.argumentsWritePosition ??
                new monacoEditor.Position(
                  range.startLineNumber,
                  range.startColumn + 1,
                ),
            };
            path.argumentsRange = newFields.argumentsRange;
            path.argumentsWritePosition = newFields.argumentsWritePosition;
          }
        }
        break;
      }
      case 'Argument': {
        if (!state.name) throw new Error('argument is missing name');
        const fieldState = state.prevState?.prevState;
        if (!fieldState) {
          // shouldn't happen, argument should always have prevState.prevState
          throw new Error('missing prevState.prevState');
        }
        const parentMatch = doesPathMatch(fieldState, docsPath, schema);
        if (parentMatch.matched && parentMatch.remainingDocsPath.length === 0) {
          const editorNodes = parsePathToOperationPath(
            getAncestorStates(fieldState),
          );
          const pathKey = serializeOperationPath(editorNodes);
          let path = partialPaths[pathKey];

          if (!path) {
            const containsUntypedInlineFragment = editorNodes.some(
              (node) => node.kind === 'InlineFragment' && !node.type,
            );
            const newPath = {
              ...(containsUntypedInlineFragment
                ? {
                    inlineFragmentSiblings: [],
                    closestUntypedInlineFragmentKey:
                      getClosestUntypedInlineFragmentKey(editorNodes),
                  }
                : { arguments: [] }),
              isUntypedInlineFragment: isUntypedInlineFragment(
                editorNodes[editorNodes.length - 1],
              ),
              closestTypedParentKey: getClosestTypedParentKey(editorNodes),
            };
            path = newPath;
          }

          partialPaths[pathKey] = path;

          const variable = tokens
            .slice()
            .reverse()
            .find((t) => t.state.kind === 'Variable')?.state;

          if ('inlineFragmentSiblings' in path) {
            makeNewInlineFragmentSiblingIfPreviousSiblingVisited(path);
            path.inlineFragmentSiblings = [
              ...path.inlineFragmentSiblings.slice(0, -1),
              {
                ...path.inlineFragmentSiblings[
                  path.inlineFragmentSiblings.length - 1
                ],
                arguments: [
                  ...(path.inlineFragmentSiblings[
                    path.inlineFragmentSiblings.length - 1
                  ]?.arguments ?? []),
                  {
                    name: state.name,
                    range,
                    ...(variable
                      ? { isVariable: true, value: variable.name ?? '' }
                      : { isVariable: false, value: null }),
                  },
                ],
                argumentsWritePosition: range.getEndPosition(),
              },
            ];
          } else {
            path.arguments.push({
              name: state.name,
              range,
              ...(variable
                ? { isVariable: true, value: variable.name ?? '' }
                : { isVariable: false, value: null }),
            });
            path.argumentsWritePosition = range.getEndPosition();
          }
        }
        break;
      }

      case 'VariableDefinitions': {
        variableDefinitionRange = range;
        break;
      }
      default:
        // Ignore node
        break;
    }
  });

  const lastVariable = variables[variables.length - 1];
  if (lastVariable) {
    variableWritePosition = lastVariable.range.getEndPosition();
  } else if (variableDefinitionRange) {
    variableWritePosition = new monacoEditor.Position(
      variableDefinitionRange.startLineNumber,
      variableDefinitionRange.startColumn + 1,
    );
  }

  const baseDocsContext = {
    variables,
    variableDefinitionRange,
    variableOperationCount,
    variableWritePosition,
    definitionKind,
  };

  // If we are on the Query page in the docs, matchType = 'root'
  if (fullDocsPath.length === 0) {
    return {
      matchType: 'root',
      selectionSet: [
        {
          name: selectedDefinition.type,
          type: 'operation',
          range: selectedDefinition.range,
        },
      ],
      writePosition: selectedDefinition.range.getEndPosition(),
      remainingDocsPath: [],
      indentLevel: 0,
      arguments: [],
      ...baseDocsContext,
    };
  }

  /**
   * This function does 2 things in 1 loop:
   *
   * 1. Since we match based on cursor for inline fragments, we keep sibling paths
   * with inline fragments in their path in arrays.
   * This function goes through the sibling options and filters out the ones
   * that do not contain a cursor.
   *
   * 2. Inline fragments need to have argumentWritePosition, argument & argumentsRange
   * of their closest non inline fragment parent, because arguments will be written
   * on the parent, not on the inline fragment.
   */
  const getCorrectedPathWithArguments = memoize(
    (
      value: InlineFragmentPartialPath | PartialPath,
    ): PartialPath | undefined => {
      if ('inlineFragmentSiblings' in value) {
        const closestParentInlineFragments =
          partialPaths[value.closestUntypedInlineFragmentKey];
        if (
          !closestParentInlineFragments ||
          !('inlineFragmentSiblings' in closestParentInlineFragments)
        ) {
          return undefined;
        }
        const closestParentInlineFragmentWithCursor =
          closestParentInlineFragments.inlineFragmentSiblings.find(
            (sibling) =>
              sibling.range &&
              exclusiveContainsPosition(sibling.range, cursorPosition),
          );
        if (!closestParentInlineFragmentWithCursor) {
          return undefined;
        }
        const withCursor = value.inlineFragmentSiblings.find(
          (sibling) =>
            sibling.range &&
            closestParentInlineFragmentWithCursor.range?.containsRange(
              sibling.range,
            ),
        );

        if (withCursor) {
          const newVal = {
            ...omit(value, 'inlineFragmentSiblings'),
            ...withCursor,
          };
          /**
           * Inline fragments need to have argumentWritePosition, argument & argumentsRange
           * of their closest non inline fragment parent, because arguments will be written
           * on the parent, not on the inline fragment.
           */
          if (value.isUntypedInlineFragment && value.closestTypedParentKey) {
            const closestTypedParentKey =
              partialPaths[value.closestTypedParentKey];
            const nonInlineParent = closestTypedParentKey
              ? getCorrectedPathWithArguments(closestTypedParentKey)
              : undefined;

            if (nonInlineParent) {
              return {
                ...newVal,
                arguments: nonInlineParent.arguments,
                argumentsWritePosition: nonInlineParent.argumentsWritePosition,
                argumentsRange: nonInlineParent.argumentsRange,
              };
            }
          }
          return newVal;
        }
        return undefined;
      }
      return value;
    },
  );

  const paths = Object.values(partialPaths)
    .map((partialPath): Path | undefined => {
      const correctedValue = getCorrectedPathWithArguments(partialPath);

      if (!correctedValue) return undefined;
      const { range, match, writePosition, ...rest } = correctedValue;
      // Every path should have a `SelectionSet` and field, fragment, or operation
      // and so should be a full `SelectionSet`. This is removing the `Partial` that
      // was needed for building the nodes
      if (!range || !match || !writePosition) {
        throw new Error('path is invalid');
      }
      return { ...rest, range, match, writePosition };
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  /**
   * Restrict the range to search for matches to:
   * 1. the inline fragment that the cursor is in if the cursor is in one
   * 2. the common ancestor between where the cursor is and the docs state
   * 3. the range of the selected definition
   */
  const rangeToSearchForMatches =
    innerMostMatchingInlineFragmentWithCursor?.range ??
    // cursor and docs common ancestor
    paths.reduce<Path | undefined>((ancestor, path) => {
      const isInRange = path.range.containsPosition(cursorPosition);
      if (
        isInRange &&
        (!ancestor ||
          ancestor.match.remainingDocsPath.length >
            path.match.remainingDocsPath.length ||
          (path.match.depth < ancestor.match.depth &&
            ancestor.match.remainingDocsPath.length ===
              path.match.remainingDocsPath.length))
      ) {
        return path;
      }
      return ancestor;
    }, undefined)?.range ??
    selectedDefinition.range;

  /**
   * Of all operation paths, find the path that has the closest match.
   * Closest match here means the path with the most path segments in common
   * with the docs state.
   */
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const match = paths.reduce<Path | undefined>((bestMatch, path) => {
    if (!path.match.matched) {
      return bestMatch;
    }
    if (rangeToSearchForMatches.containsRange(path.range)) {
      if (!bestMatch) {
        return path;
      }
      const matchDocsLength = path.match.remainingDocsPath.length;
      const bestDocsLength = bestMatch.match.remainingDocsPath.length;
      if (
        // if this is an inline fragment & the cursor is not in it,
        // never match on it
        !(
          path.isUntypedInlineFragment &&
          !exclusiveContainsPosition(path.range, cursorPosition)
        ) &&
        (matchDocsLength < bestDocsLength ||
          (matchDocsLength === bestDocsLength && 'selectionSet' in path))
      ) {
        return path;
      }
    }
    return bestMatch;
  }, undefined)!;

  const matchContext = {
    writePosition: match.writePosition,
    remainingDocsPath: match.match.remainingDocsPath,
    arguments: match.arguments,
    argumentsRange: match.argumentsRange,
    argumentsWritePosition: match.argumentsWritePosition,
    indentLevel: inferIndentLevel(
      match.range.getStartPosition(),
      model.getLinesContent(),
    ),
  };

  if (match.selectionSet) {
    return {
      matchType: 'full',
      selectionSet: match.selectionSet,
      ...baseDocsContext,
      ...matchContext,
    };
  }

  return {
    matchType: 'partial',
    ...baseDocsContext,
    ...matchContext,
  };
}
